// ============================================================
// CareChargeRolePanel.jsx  (role-model expansion Push 1, 2026-07-13)
// User-detail panel for managing a user's CareCharge MODULE role
// (Finance / Finance Manager). Rendered inside the Agent Access tab of
// <UserDetailPage>, beside <CarePeopleRolePanel>. Clone of that panel
// MINUS the tier machinery (finance has a single access level — F1);
// reuses the cp-role-panel styling.
//
// Self-gating: fetches GET .../carecharge-role, which returns
// `coreRoleApplicable` (FE1: admin + manager; carer is frontline and
// never finance-grantable, auditor is read-only).
//
// finance_manager is the module-CONFIG tier (ratified 2026-07-12): for a
// base manager it restores CareCharge settings write under the strict
// re-gate; for an admin it also lights the CareCharge sidebar entry.
//
// Backend is the source of truth — no optimistic UI. Every mutation
// awaits the API then re-fetches the panel.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertCircle, RotateCw, ShieldCheck } from 'lucide-react';
import Badge from './Badge';
import ConfirmDialog from './ConfirmDialog';
import CareChargeRoleAssignModal from './CareChargeRoleAssignModal';
import { toast } from './Toaster';
import { getCareChargeRole, grantCareChargeRole, revokeCareChargeRole } from '../api/careChargeRoles';
import { translateError } from '../utils/errorMessages';
import StatusMessage from './StatusMessage';
import { formatDate } from '../utils/format';
import './CarePeopleRolePanel.css';

const ROLE_LABEL = { finance: 'Finance', finance_manager: 'Finance Manager' };

function roleLabel(role) {
  return ROLE_LABEL[role] || role || '';
}

export default function CareChargeRolePanel({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState(null);

  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getCareChargeRole(user.clientUserId);
      setData(res);
    } catch (err) {
      setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [user.clientUserId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  function openAssign() {
    setAssignError(null);
    setAssignOpen(true);
  }

  async function handleAssignSubmit({ careChargeRole }) {
    setAssignSubmitting(true);
    setAssignError(null);
    try {
      await grantCareChargeRole(user.clientUserId, { careChargeRole });
      toast.success('CareCharge role assigned');
      setAssignOpen(false);
      await fetchData();
    } catch (err) {
      // Keep the modal open with an inline error so the admin can correct + retry.
      setAssignError(translateError(err));
    } finally {
      setAssignSubmitting(false);
    }
  }

  async function handleRevokeConfirm() {
    setRevokeSubmitting(true);
    try {
      await revokeCareChargeRole(user.clientUserId);
      toast.success('CareCharge role revoked');
      setRevokeOpen(false);
      await fetchData();
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setRevokeSubmitting(false);
    }
  }

  if (loading && !data) {
    return (
      <StatusMessage variant="loading" className="cp-role-panel__state">
        <Loader2 size={16} className="cp-role-panel__spinner" />
        <span>Loading CareCharge role…</span>
      </StatusMessage>
    );
  }

  if (error) {
    return (
      <StatusMessage variant="error" className="cp-role-panel__state cp-role-panel__state--error">
        <AlertCircle size={16} />
        <span>{error}</span>
        <button type="button" className="cp-role-panel__retry" onClick={fetchData}>
          <RotateCw size={14} />
          Retry
        </button>
      </StatusMessage>
    );
  }

  if (!data) return null;

  // carer / auditor — the finance overlay does not apply (FE1).
  if (!data.coreRoleApplicable) {
    return (
      <section className="cp-role-panel cp-role-panel--na" data-testid="cc-role-panel-na">
        <h3 className="cp-role-panel__heading">CareCharge Module Role</h3>
        <div className="cp-role-panel__na-note">
          <ShieldCheck size={16} />
          <p>
            Finance roles apply only to admin and manager users — carers are
            frontline and auditors are read-only.
          </p>
        </div>
      </section>
    );
  }

  const current = data.current;
  const history = data.history || [];

  return (
    <section className="cp-role-panel" data-testid="cc-role-panel">
      <h3 className="cp-role-panel__heading">CareCharge Module Role</h3>

      <div className="cp-role-panel__current">
        <div className="cp-role-panel__current-main">
          <span className="cp-role-panel__current-label">Current role</span>
          {current ? (
            <span className="cp-role-panel__current-value" data-testid="cc-role-current">
              <Badge variant="teal">{roleLabel(current.careChargeRole)}</Badge>
            </span>
          ) : (
            <span className="cp-role-panel__current-value" data-testid="cc-role-current">
              <Badge variant="neutral">None</Badge>
            </span>
          )}
        </div>
        {current && (
          <p className="cp-role-panel__current-meta">
            Assigned {formatDate(current.grantedAt)}{current.grantedBy && <> by {current.grantedBy}</>}
          </p>
        )}
        <div className="cp-role-panel__actions">
          <button type="button" className="cp-role-panel__change" onClick={openAssign}>
            {current ? 'Change role' : 'Assign role'}
          </button>
          {current && (
            <button
              type="button"
              className="cp-role-panel__revoke"
              onClick={() => setRevokeOpen(true)}
            >
              Revoke access
            </button>
          )}
        </div>
      </div>

      <div className="cp-role-panel__history" data-testid="cc-role-history">
        <h4 className="cp-role-panel__history-heading">Role history</h4>
        {history.length === 0 ? (
          <p className="cp-role-panel__history-empty">No role changes recorded yet.</p>
        ) : (
          <ul className="cp-role-panel__history-list">
            {history.map((h, i) => {
              const vals = h.action === 'granted' ? h.newValues : h.oldValues;
              const desc = vals ? roleLabel(vals.careChargeRole) : '';
              return (
                <li key={`${h.changedAt}-${i}`} className="cp-role-panel__history-row">
                  <span className="cp-role-panel__history-line">
                    <Badge variant={h.action === 'granted' ? 'success' : 'danger'}>
                      {h.action === 'granted' ? 'Granted' : 'Revoked'}
                    </Badge>
                    <span className="cp-role-panel__history-role">{desc}</span>
                    <span className="cp-role-panel__history-date">{formatDate(h.changedAt)}</span>
                  </span>
                  {h.changedBy && (
                    <span className="cp-role-panel__history-by">by {h.changedBy}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <CareChargeRoleAssignModal
        isOpen={assignOpen}
        onClose={() => { if (!assignSubmitting) setAssignOpen(false); }}
        onSubmit={handleAssignSubmit}
        submitting={assignSubmitting}
        errorMessage={assignError}
        initialRole={current ? current.careChargeRole : 'finance'}
      />

      <ConfirmDialog
        isOpen={revokeOpen}
        onClose={() => { if (!revokeSubmitting) setRevokeOpen(false); }}
        onConfirm={handleRevokeConfirm}
        title={current ? `Revoke ${roleLabel(current.careChargeRole)} access?` : 'Revoke access?'}
        message={
          current
            ? `This will remove ${user.email || 'this user'}'s CareCharge access immediately. The removal is final: CareHub will not give it back automatically, even though managers get finance by default. You can grant it again here later.`
            : ''
        }
        confirmLabel="Confirm revoke"
        isSubmitting={revokeSubmitting}
      />
    </section>
  );
}
